/**
 * RoleDetailModal - Redesigned Role Modal
 *
 * Modern, professional edit modal using EditModal with:
 * - Tab-based navigation
 * - Consistent form field styling
 * - Keyboard shortcuts
 * - Unsaved changes detection
 */

import React, { useEffect, useState, useCallback } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import {
	BaseForm,
	SwitchBox,
	TextAreaBox,
	TextBox,
} from '../../../../components/base';
import { EditModal, EditPageField } from '../../../../components/container';
import { RoleModel, df_RoleModel, valid_RoleModel } from '../../../../context/models/roles/RoleModel';
import { CheckedBoxListRoleScope } from '../../../../components/shared/system';
import { Shield, Lock, Info } from 'lucide-react';

type IRoleDetailModalProps = {
	isShow: boolean;
	title: string;
	detailModel?: RoleModel;
	isSaving: boolean | false;
	onClose: () => void;
	onSubmit: (data: RoleModel) => void;
};

const RoleDetailModal = (props: IRoleDetailModalProps) => {
	const { isShow, title, detailModel, onSubmit, onClose, isSaving } = props;
	const isAddMode = !detailModel;
	const [initialValues, setInitialValues] = useState<RoleModel>(df_RoleModel);
	const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

	const {
		handleSubmit,
		reset,
		formState: { errors },
	} = useForm<RoleModel>({
		resolver: yupResolver(valid_RoleModel() as any),
		mode: 'all',
		defaultValues: initialValues,
	});

	const onSubmitHandler = (values: RoleModel) => {
		onSubmit(values);
		setHasUnsavedChanges(false);
	};

	useEffect(() => {
		if (initialValues) {
			reset(initialValues);
		}
		//eslint-disable-next-line react-hooks/exhaustive-deps
	}, [initialValues, reset]);

	useEffect(() => {
		if (detailModel) {
			const { id, name, description, isActive, isProtected, roleScopeIds } = detailModel;
			setInitialValues((prev) => ({ ...prev, id, name, description, isActive, isProtected, roleScopeIds }));
			setHasUnsavedChanges(false);
		}
		//eslint-disable-next-line react-hooks/exhaustive-deps
	}, [detailModel]);

	// Helper to update field and mark as unsaved
	const updateField = useCallback((field: keyof RoleModel, value: any) => {
		setInitialValues((prev) => ({ ...prev, [field]: value }));
		setHasUnsavedChanges(true);
	}, []);

	// Tab content: Basic Info
	const basicInfoTab = (
		<div className='space-y-4'>
			<EditPageField
				label='Tên vai trò'
				required
				error={errors.name?.message}
				helpText='Tên hiển thị của vai trò'
			>
				<TextBox
					name='name'
					error={errors.name?.message}
					onValueChanged={(value) => updateField('name', value)}
					value={initialValues.name}
					hasValid={true}
					placeholder='Nhập tên vai trò...'
				/>
			</EditPageField>

			<EditPageField
				label='Mô tả'
				error={errors.description?.message}
				helpText='Mô tả chi tiết về vai trò'
			>
				<TextAreaBox
					name='description'
					error={errors.description?.message}
					onValueChanged={(value) => updateField('description', value)}
					value={initialValues.description}
					hasValid={true}
					placeholder='Mô tả...'
				/>
			</EditPageField>

			<div className='space-y-3'>
				<EditPageField
					label='Trạng thái'
				>
					<SwitchBox
						name='isActive'
						label='Áp dụng vai trò này'
						error={errors.isActive?.message}
						onValueChanged={(value) => updateField('isActive', value)}
						value={initialValues.isActive}
						sizeClass='sm'
					/>
				</EditPageField>

				<EditPageField
					label='Bảo vệ'
				>
					<SwitchBox
						name='isProtected'
						label='Không cho phép sửa đổi vai trò này'
						error={errors.isProtected?.message}
						onValueChanged={(value) => updateField('isProtected', value)}
						value={initialValues.isProtected}
						sizeClass='sm'
					/>
				</EditPageField>
			</div>
		</div>
	);

	// Tab content: Permissions
	const permissionsTab = (
		<div className='space-y-4'>
			<EditPageField
				label='Phạm vi quyền'
				error={errors.roleScopeIds?.message}
				helpText='Chọn các phạm vi quyền cho vai trò này'
			>
				<CheckedBoxListRoleScope
					name='roleScopeIds'
					hasValid={true}
					error={errors.roleScopeIds?.message}
					selectedValue={initialValues.roleScopeIds}
					onSelectionChanged={(value: any[]) => {
						updateField('roleScopeIds', value?.map((x) => parseInt(x)));
					}}
				/>
			</EditPageField>
		</div>
	);

	const tabs = [
		{
			id: 'basic',
			label: 'Thông tin cơ bản',
			icon: <Shield className='h-4 w-4' />,
			content: basicInfoTab,
		},
		{
			id: 'permissions',
			label: 'Phạm vi quyền',
			icon: <Lock className='h-4 w-4' />,
			content: permissionsTab,
		},
	];

	return (
		<EditModal
			isOpen={isShow}
			onClose={onClose}
			title={title}
			description={isAddMode ? 'Tạo mới vai trò' : 'Chỉnh sửa vai trò'}
			sizeClass='md'
			tabs={tabs}
			isSaving={isSaving}
			onSave={handleSubmit(onSubmitHandler as any)}
			onCancel={onClose}
			hasUnsavedChanges={hasUnsavedChanges}
			breadcrumbs={[
				{ label: 'Trang chủ', href: '/' },
				{ label: 'Vai trò', href: '/roles' },
				{ label: isAddMode ? 'Tạo mới' : detailModel?.name || 'Chỉnh sửa' },
			]}
			status={isAddMode ? 'Tạo mới' : undefined}
			statusVariant='default'
		/>
	);
};

export default RoleDetailModal;
